import classNames from "classnames";
import type { Status } from "discord.js";
import { useEffect, useState } from "react";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { WithTooltipPortaled } from "../tooltips/with_tooltip_portaled";

export function AdminShardsViewer() {
	const shards_query = trpc.admin.getShardsStatus.useQuery(undefined, {
		refetchInterval: 10000,
	});

	if (!shards_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="grid w-1/2 grid-cols-2 gap-x-4 text-left">
				<div className="text-content-secondary">Shards</div>
				<div>{shards_query.data.shardDetails.length}</div>
				<div className="text-content-secondary">Ping Mean</div>
				<div>{shards_query.data.averagePing}</div>
				<div className="text-content-secondary">
					Ping Std. Deviation
				</div>
				<div>{shards_query.data.standardDeviation}</div>
			</div>
			<NextFetchTime
				dataUpdatedAt={shards_query.dataUpdatedAt}
				isFetching={shards_query.isFetching}
			/>
			<div className="flex flex-wrap">
				{shards_query.data.shardDetails.map((val) => (
					<ShardCell key={val.id} {...val} />
				))}
			</div>
		</div>
	);
}

function NextFetchTime(props: { dataUpdatedAt: number; isFetching: boolean }) {
	const [fetchTime, setFetchTime] = useState(
		10000 - (new Date().getTime() - props.dataUpdatedAt)
	);

	useEffect(() => {
		const interval = setInterval(() => {
			setFetchTime(10000 - (new Date().getTime() - props.dataUpdatedAt));
		}, 1000);

		return () => clearInterval(interval);
	}, [props.dataUpdatedAt]);

	const displayTime = Math.max(Math.round(fetchTime / 1000), 0);

	return (
		<div className="text-right text-sm">
			{props.isFetching || displayTime === 0
				? "Refetching now..."
				: `Retching in ${displayTime} seconds...`}
		</div>
	);
}

function ShardCell(
	props: RouterInferenceHelpers["admin"]["getShardsStatus"]["output"]["shardDetails"][0]
) {
	const restart_shard_mutation = trpc.admin.restartShard.useMutation();

	return (
		<WithTooltipPortaled
			no_delay
			tooltip={
				<div className="flex flex-col gap-2">
					<div className="grid grid-cols-2 gap-x-4 text-left">
						<div className="text-content-secondary">ID</div>
						<div>{props.id}</div>
						<div className="text-content-secondary">Status</div>
						<div>{props.status}</div>
						<div className="text-content-secondary">Ping</div>
						<div>{props.ping}</div>
						<div className="text-content-secondary">Guilds</div>
						<div>{props.guild_count}</div>
					</div>
					<div>
						<SeshPrimaryButton
							is_loading={restart_shard_mutation.isLoading}
							custom_size="small"
							onClick={() =>
								restart_shard_mutation.mutate({
									shard_id: props.id,
								})
							}
						>
							Restart
						</SeshPrimaryButton>
					</div>
				</div>
			}
			style={"Info"}
		>
			<div
				className={classNames(
					"flex h-14 w-14 flex-col items-center justify-center rounded-md border border-base-light hover:border-sesh-primary",
					{
						"bg-base-dark": props.status === "Ready",
						"bg-warning": props.status !== "Ready",
					}
				)}
			>
				<div>{props.id}</div>
			</div>
		</WithTooltipPortaled>
	);
}
